'use client';

import { MONO_FONT } from '@/theme';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { getRelatedArticles } from '@/libs/content/articles';

export default function ArticleRelated({ current }) {
  const related = getRelatedArticles(current);

  if (!related.length) return null;

  return (
    <Box
      component="section"
      sx={{
        borderTop: '1px solid',
        borderColor: 'grey.100',
        py: { xs: 7, md: 10 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: '20px', sm: '32px' },
        }}
      >
        {/* Section label (h2: da jerarquía a los títulos h3 de las tarjetas) */}
        <Box
          component="h2"
          sx={{
            m: 0,
            fontWeight: 400,
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'text.muted',
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            '&::after': {
              content: '""',
              flex: 1,
              height: '1px',
              bgcolor: 'grey.100',
            },
          }}
        >
          Sigue leyendo · Posts relacionados
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: related.length >= 2 ? '1fr 1fr' : '1fr',
              md:
                related.length >= 3
                  ? 'repeat(3, 1fr)'
                  : related.length === 2
                    ? '1fr 1fr'
                    : '1fr',
            },
            gap: { xs: 3, md: 4 },
          }}
        >
          {related.map((article) => (
            <RelatedCard key={article.slug} article={article} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function RelatedCard({ article }) {
  return (
    <Box
      component={Link}
      href={`/blog/${article.slug}`}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '6px',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'background.default',
        transition: 'border-color 0.15s ease, transform 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
        },
        '&:hover .related-title': {
          color: 'text.accent',
        },
      }}
    >
      {/* Miniatura a la izquierda: ancho fijo, se estira a la altura del card */}
      {article.image?.src && (
        <Box
          sx={{
            position: 'relative',
            alignSelf: 'stretch',
            width: { xs: 104, sm: 116, md: 132 },
            flexShrink: 0,
            bgcolor: 'grey.100',
          }}
        >
          <Box
            component="img"
            src={article.image.src}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {/* Avatar del autor — badge sobre la miniatura */}
          {article.author.avatar ? (
            <Box
              component="img"
              src={article.author.avatar}
              alt={article.author.name}
              sx={{
                position: 'absolute',
                right: '8px',
                bottom: '8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'primary.darker',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                right: '8px',
                bottom: '8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'primary.darker',
                bgcolor: 'primary.darker',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: MONO_FONT,
                fontSize: '9px',
                fontWeight: 600,
                color: 'primary.glow',
              }}
            >
              {article.author.initials}
            </Box>
          )}
        </Box>
      )}

      {/* Contenido */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: { xs: '20px', md: '24px' },
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Title */}
        <Typography
          className="related-title"
          component="h3"
          sx={{
            fontSize: 'clamp(15px, 1.2vw, 18px)',
            fontWeight: 500,
            letterSpacing: '-0.012em',
            lineHeight: 1.3,
            color: 'text.primary',
            transition: 'color 0.15s ease',
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '& .accent': { color: 'text.accent' },
          }}
          dangerouslySetInnerHTML={{ __html: article.title }}
        />

        {/* Meta */}
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.muted',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'grey.100',
            mt: 'auto',
          }}
        >
          <Box component="span" sx={{ color: 'text.accent', fontWeight: 600 }}>
            {article.category}
          </Box>
          <Box component="span" sx={{ color: 'grey.300' }}>
            ·
          </Box>
          <span>{article.readTime}</span>
        </Box>
      </Box>
    </Box>
  );
}
